import { useRef } from 'react';
import { PageHeader } from '@/components/page-header';
import { planName } from '@/shared/config/plans';
import { useEntranceMotion } from '@/shared/hooks/use-entrance-motion';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { useBillingActions } from '../hooks/use-billing-actions';
import { pastDueDeadline, paymentMethodLabel } from '../model/billing-status';
import type { WorkspaceData } from '../types';
import { InvoicesCard } from './invoices-card';
import { PaymentDetailsCard } from './payment-details-card';
import { PlanSummaryCard } from './plan-summary-card';
import { UsageCard } from './usage-card';

type BillingViewProps = { workspace: WorkspaceData };

/** Plan, Pod-hours, payment details and invoices (design: Usage). */
export function BillingView({ workspace }: BillingViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEntranceMotion(rootRef, 'billing');
  const { billing } = workspace;
  const actions = useBillingActions(billing.paddle);
  const subscription = billing.subscription;
  const isPastDue = subscription?.status === 'PAST_DUE';
  const deadline = subscription ? pastDueDeadline(subscription) : null;
  const name = planName(workspace.plan);

  return (
    <div ref={rootRef} className="contents">
      <PageHeader
        title="Billing"
        description="Paddle handles payment, tax and invoices as our merchant of record."
      />
      {isPastDue ? (
        <div data-enter="" role="alert">
          <Banner
            tone="bad"
            title={`We could not charge ${paymentMethodLabel(billing.paymentMethod)} for ${name}.`}
            action={
              <Button
                variant="primary"
                isLoading={actions.isUpdatingMethod}
                onClick={() => void actions.updatePaymentMethod()}
              >
                Update payment method
              </Button>
            }
          >
            {deadline
              ? `Update the card by ${deadline} to keep ${name} active.`
              : `Update the card to keep ${name} active.`}
          </Banner>
        </div>
      ) : null}
      <PlanSummaryCard workspace={workspace} />
      <UsageCard workspace={workspace} />
      <PaymentDetailsCard
        workspace={workspace}
        isUpdating={actions.isUpdatingMethod}
        onUpdatePaymentMethod={() => void actions.updatePaymentMethod()}
      />
      <InvoicesCard
        isPaid={subscription !== null}
        payingInvoiceId={actions.payingInvoiceId}
        onPay={(invoice) => void actions.payInvoice(invoice)}
        onPdf={(invoice) => void actions.openInvoicePdf(invoice)}
      />
    </div>
  );
}
